import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ExecutionResult } from '@apollo/react-common';
import type { ApolloError } from 'apollo-client';

import type { Provider } from '@atlaskit/collab-provider';
import type { ProviderError, CollabEditProvider } from '@atlaskit/editor-common/collab';
import { PROVIDER_ERROR_CODE } from '@atlaskit/editor-common/collab';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isApolloError } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { retry } from '@confluence/retry';
import {
	EDIT_PAGE_EXPERIENCE,
	EDIT_PAGE_LOAD_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperimentalResetCollabServiceResp } from '@confluence/fabric-providers/entry-points/ResetCollabServiceMutation.ts';
import { BadStatusError } from '@confluence/network';
import { getStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { getTraceIdFromApolloError } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

type NativeCollabError = {
	error: ProviderError | null;
	recoveryError: Error | CollabRecoveryErrorType | null;
};

export class CollabRecoveryErrorType extends Error {
	constructor(
		public message: string,
		public status?: number,
		public traceId?: string,
	) {
		super(message);
		this.status = status;
		this.traceId = traceId;
	}
}

export interface ICollabRecoveryErrorType {
	message: string;
	status: number;
	traceId: string;
}

const isNotPermittedError = (error: ProviderError) => {
	return (
		(error.code === PROVIDER_ERROR_CODE.NO_PERMISSION_ERROR && 'EXPIRED_TOKEN' !== error.reason) ||
		error.code === PROVIDER_ERROR_CODE.DOCUMENT_NOT_FOUND
	);
};

export const isUnrecoverableError = (error: ProviderError) => {
	return !error.recoverable && PROVIDER_ERROR_CODE.INTERNAL_SERVICE_ERROR !== error.code;
};

const getStatusCodeFromApolloError = (error: ApolloError) => {
	return isApolloError(error) &&
		BadStatusError.isBadStatusError(error?.graphQLErrors?.[0]?.originalError)
		? Number(getStatusCodeError(error!.graphQLErrors[0]!.originalError))
		: null;
};

const isTenantSuspendedError = (error: ProviderError) => error.status === 423;

/**
 * Listens for collab service error & provides error to caller. Some errors may have additional logic before being returned.
 * Error is emitted after the collab service is initialised.
 */
export const useError = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	resetCollabService: () => Promise<ExecutionResult<ExperimentalResetCollabServiceResp>>,
	contentId: string,
	contentType: string,
): NativeCollabError => {
	const [error, setError] = useState<ProviderError | null>(null);
	const [recoveryError, setRecoveryError] = useState<Error | null>(null);
	const hasResetRef = useRef<{ resetInProgress: boolean; resetCount: number }>({
		resetInProgress: false,
		resetCount: 0,
	});
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const currentEditorPageLoadExperienceName = isLivePage
		? VIEW_PAGE_EXPERIENCE
		: EDIT_PAGE_LOAD_EXPERIENCE;
	const stopExperienceOnRecoveryError = useCallback(
		(recoveryErrorForAnalytics: any) => {
			if (!!recoveryErrorForAnalytics?.status && recoveryErrorForAnalytics?.status < 500) {
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: `Recovery failed with error status ${recoveryErrorForAnalytics.status}`,
					attributes: {
						errorStatus: recoveryErrorForAnalytics?.status,
						traceId: recoveryErrorForAnalytics?.traceId,
						objectId: contentId,
						objectType: contentType,
						isLivePage,
					},
				});
				return;
			}
			experienceTracker.stopOnError({
				name: currentEditorPageLoadExperienceName,
				error: recoveryErrorForAnalytics,
				attributes: {
					...(recoveryErrorForAnalytics?.status
						? { errorStatus: recoveryErrorForAnalytics.status }
						: {}),
					errorType: 'Recovery failed',
					traceId: recoveryErrorForAnalytics?.traceId,
					objectId: contentId,
					objectType: contentType,
					isLivePage,
				},
			});
		},
		[contentId, contentType, experienceTracker, currentEditorPageLoadExperienceName, isLivePage],
	);

	const setErrorAndDisconnect = useCallback(
		(newError: ProviderError) => {
			if (
				isNotPermittedError(newError) ||
				isUnrecoverableError(newError) ||
				isTenantSuspendedError(newError)
			) {
				setError(newError);
			}

			const experienceState = experienceTracker.getExperienceState(
				currentEditorPageLoadExperienceName,
			);
			if (!experienceState || experienceState.hasStopped) {
				// initialisation completed but we received an error later
				if (!isNotPermittedError(newError)) {
					if (isUnrecoverableError(newError)) {
						experienceTracker.stopOnError({
							name: EDIT_PAGE_EXPERIENCE,
							error: {
								name: newError.code,
								message: newError.message,
							},
							attributes: {
								errorStatus: newError.status,
								objectId: contentId,
								objectType: contentType,
								isLivePage,
							},
						});
					} else {
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'error',
								actionSubject: 'nativeEditor',
								source: 'confluenceFrontend',
								objectId: contentId,
								objectType: contentType,
								attributes: {
									errorName: newError.code,
									errorMessage: newError.message,
									errorStatus: newError.status,
									isRecoverable: true,
									reason: 'reason' in newError ? newError.reason : 'unknown',
									isLivePage,
								},
							},
						}).fire();
					}
				}
				return;
			}

			if (isNotPermittedError(newError)) {
				// expected 403 for restricted page or deleted page
				// for deleted page, 403 is returned with reason "RESOURCE_DELETED"
				let newErrorReason: string | undefined;
				if ('reason' in newError) {
					newErrorReason = newError.reason;
				}
				collabEditProvider!.disconnect();
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: newErrorReason ?? 'page restricted or deleted',
					attributes: {
						errorStatus: newError.status,
						errorName: newError.code,
						errorMessage: newError.message,
						objectId: contentId,
						objectType: contentType,
						isLivePage,
					},
				});
			} else if (isUnrecoverableError(newError)) {
				// unhandled/unexpected errors
				experienceTracker.stopOnError({
					name: currentEditorPageLoadExperienceName,
					error: {
						name: newError.code,
						message: newError.message,
					},
					attributes: {
						errorStatus: newError.status,
						objectId: contentId,
						objectType: contentType,
						isLivePage,
					},
				});
			}
		},
		[
			collabEditProvider,
			experienceTracker,
			createAnalyticsEvent,
			contentId,
			contentType,
			currentEditorPageLoadExperienceName,
			isLivePage,
		],
	);

	const collabRecovery = useCallback(async () => {
		if (hasResetRef.current.resetInProgress) {
			return;
		} else if (hasResetRef.current.resetCount > 0) {
			// recovery has already been attempted but we've received another 404d
			const error = new CollabRecoveryErrorType(`Collab recovery unsuccessful`);
			setRecoveryError(error);
			stopExperienceOnRecoveryError(error);
			return;
		}
		hasResetRef.current = {
			resetInProgress: true,
			resetCount: (hasResetRef.current.resetCount += 1),
		};

		const shouldRetryPredicate = async (error: any): Promise<boolean> => {
			const statusCode = getStatusCodeFromApolloError(error);
			return !!statusCode && statusCode >= 500;
		};

		try {
			await retry(resetCollabService, { maxAttempts: 2, timeout: 2000 }, shouldRetryPredicate); // kick off page recovery, with retry
			hasResetRef.current.resetInProgress = false;
		} catch (e) {
			// page recovery has failed, editor is empty... fatal error
			let recoveryError = e; // "public" error used by other components
			const statusCode = getStatusCodeFromApolloError(e);
			if (!!statusCode) {
				recoveryError = new CollabRecoveryErrorType(
					`Collab recovery responded with ${statusCode}`,
					statusCode,
					getTraceIdFromApolloError(e) ?? undefined,
				);
			}

			setRecoveryError(recoveryError);
			stopExperienceOnRecoveryError(recoveryError);
			hasResetRef.current.resetInProgress = false;
		}
	}, [resetCollabService, stopExperienceOnRecoveryError]);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleError = (emittedError: ProviderError) => {
			if (emittedError.status == 404) {
				// 404 means that collab service doesn't have the document but Confluence does
				// try reset the document before displaying error (will be created in collab service if it doesn't exist). Currently only attempt reset once (no retry)
				void collabRecovery();
			} else {
				setErrorAndDisconnect(emittedError);
			}
		};
		collabEditProvider.on('error', handleError);

		return () => {
			collabEditProvider.off('error', handleError);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collabEditProvider, resetCollabService]);

	return {
		error,
		recoveryError,
	};
};
